import { Box, Flex } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import type React from 'react';
import { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import type { CustomersReviewsVariant } from '..';
import { CUSTOMERS_REVIEWS_DEFAULTS } from '../defaults';
import type { Tweet } from './TweetCard';
import { TweetCard } from './TweetCard';

const TWEET_WIDTH = 408;
const TWEET_WIDTH_MODERN = 247;

const ContentWrapper = styled(Flex)`
  max-width: 100%;
`;

const SliderContainer = styled(Box)<{ readonly shortCard: boolean }>`
  width: 100%;

  .tweet-card {
    min-height: ${({ shortCard }) => (shortCard ? '0' : '31rem')};
  }
`;

const marqueeAnimation = keyframes`
    0% {
        transform: translateX(0%);
    }
    100% {
        transform: translateX(-100%);
    }
`;

const ReviewsMarqueeContainer = styled(Flex)<{
  readonly tweetCount: number;
  readonly shortCard: boolean;
  readonly variant: CustomersReviewsVariant;
}>`
  width: ${({ tweetCount, variant }) => `${tweetCount * (variant === 'modern' ? TWEET_WIDTH_MODERN : TWEET_WIDTH)}px`};
  align-self: start;
  height: auto
  position: relative;
  animation: ${marqueeAnimation} ${({ variant }) => (variant === 'modern' ? '50000ms' : '35000ms')} linear infinite;

  ${MEDIA_QUERIES.desktop`
        &:hover {
            animation-play-state: paused;
        }
    `}

  ${MEDIA_QUERIES.mobile`
        animation-duration: ${({ variant }: { readonly variant: CustomersReviewsVariant }) => (variant === 'modern' ? '100000ms' : '78000ms')};
    `}
`;

export interface TweetsProps {
  readonly shortCard?: boolean;
  readonly tweets?: Tweet[];
  readonly variant?: CustomersReviewsVariant;
}

export const Tweets: React.FC<TweetsProps> = ({
  shortCard = true,
  tweets = CUSTOMERS_REVIEWS_DEFAULTS.tweets as Tweet[],
  variant = 'default',
}) => {
  const tweetList = useMemo(
    () =>
      tweets
        .map(tweet => ({ ...tweet, key: tweet.name }))
        .concat(tweets.map(tweet => ({ ...tweet, key: `${tweet.name} - cloned` }))),
    [tweets],
  );
  return (
    <ContentWrapper alignItems="center" flexWrap="wrap" justifyContent="center" mt={['30px', '45px']}>
      <SliderContainer shortCard={shortCard}>
        <ReviewsMarqueeContainer shortCard={shortCard} tweetCount={tweets.length} variant={variant}>
          {tweetList.map(tweet => {
            return (
              <div key={tweet.key}>
                <TweetCard shortCard={shortCard} tweet={tweet} variant={variant} />
              </div>
            );
          })}
        </ReviewsMarqueeContainer>
      </SliderContainer>
    </ContentWrapper>
  );
};
